import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MessageCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { formatEUR } from "../contexts/CartContext";
import { whatsappUrl, WHATSAPP_DISPLAY } from "../lib/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentResultPage({ outcome = "success" }) {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const orderId = params.get("orderId");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(Boolean(orderId));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let attempts = 0;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`);
        if (!cancelled) setOrder(res.data);
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOnce();
    // Poll once more after 2s in case the webhook is slightly delayed
    const t1 = setTimeout(async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await axios.get(`${API}/orders/${orderId}`);
        if (!cancelled) setOrder(res.data);
      } catch (e) {}
    }, 2000);
    return () => {
      cancelled = true;
      clearTimeout(t1);
    };
  }, [orderId]);

  const isPaid = order?.status === "paid";
  const success = outcome === "success" && (isPaid || !order);

  const Icon = success ? CheckCircle2 : XCircle;
  const iconColor = success ? "text-emerald-600" : "text-[#8B1E0F]";
  const title = success ? t.payment.successTitle : t.payment.failureTitle;
  const body = success ? t.payment.successBody : t.payment.failureBody;

  return (
    <div
      data-testid={`payment-${outcome}`}
      className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6 py-16"
    >
      <div className="max-w-xl w-full text-center">
        {loading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#8B1E0F] mb-6" />
            <h1 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A]">
              {t.payment.pendingTitle}
            </h1>
            <p className="mt-3 text-[#5C5C5C]">{t.payment.pendingBody}</p>
          </div>
        ) : (
          <>
            <Icon className={`h-14 w-14 ${iconColor} mx-auto mb-6`} strokeWidth={1.4} />
            <h1 className="font-serif-display text-3xl md:text-5xl text-[#1A1A1A] leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-[#5C5C5C] leading-relaxed">{body}</p>

            {order && (
              <div className="mt-10 bg-white border border-[#E5E0D8] p-6 text-left">
                <Row label={t.payment.orderRef} value={`#${order.order_code}`} testid="order-ref" />
                <Row
                  label={t.payment.status}
                  value={t.payment.statusLabels[order.status] || order.status}
                  testid="order-status"
                />
                <Row
                  label={t.checkout.total}
                  value={formatEUR(order.amount_cents)}
                  testid="order-total"
                  highlight
                />
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              {!success && (
                <a
                  href={whatsappUrl(`Hola, no he podido completar el pago del pedido. Quiero ayuda.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="payment-whatsapp"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white px-7 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  {t.payment.contactWa}
                </a>
              )}
              <Link
                to="/"
                data-testid="payment-back-home"
                className="inline-flex items-center justify-center gap-2 border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] px-7 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-colors"
              >
                {t.payment.backHome}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {error && (
              <p className="mt-6 text-xs text-[#5C5C5C]">
                {WHATSAPP_DISPLAY} · {t.payment.contactWa}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testid, highlight }) {
  return (
    <div className="flex items-baseline justify-between py-2.5 border-b border-[#E5E0D8] last:border-0">
      <span className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-[#5C5C5C]">
        {label}
      </span>
      <span
        data-testid={testid}
        className={`font-mono ${highlight ? "text-lg font-semibold text-[#8B1E0F]" : "text-sm text-[#1A1A1A]"}`}
      >
        {value}
      </span>
    </div>
  );
}
